import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as identityApi from '../../api/identity';
import { generateTraceId, logAction } from '../../utils/trace';

// ---- Async thunks ----

export const fetchIdentities = createAsyncThunk(
  'identity/fetchIdentities',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();
    try {
      logAction({ type: 'identity/fetchIdentities/pending', meta: { traceId } });
      const data = await identityApi.listIdentities({ traceId });
      logAction({ type: 'identity/fetchIdentities/fulfilled', meta: { traceId } });
      return data;
    } catch (error) {
      logAction({ type: 'identity/fetchIdentities/rejected', meta: { traceId } }, { error: error.message });
      return rejectWithValue(error.message);
    }
  }
);

export const initiateLinking = createAsyncThunk(
  'identity/initiateLinking',
  async ({ provider, returnUrl, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'identity/initiateLinking/pending', meta: { traceId } }, { provider });
      const data = await identityApi.initiateLinking(provider, returnUrl, { traceId });
      logAction({ type: 'identity/initiateLinking/fulfilled', meta: { traceId } });
      return data;
    } catch (error) {
      logAction({ type: 'identity/initiateLinking/rejected', meta: { traceId } }, { error: error.message });
      return rejectWithValue(error.message);
    }
  }
);

export const setPrimaryIdentity = createAsyncThunk(
  'identity/setPrimaryIdentity',
  async ({ identityId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'identity/setPrimaryIdentity/pending', meta: { traceId } }, { identityId });
      const data = await identityApi.setPrimaryIdentity(identityId, { traceId });
      logAction({ type: 'identity/setPrimaryIdentity/fulfilled', meta: { traceId } });
      return data;
    } catch (error) {
      logAction({ type: 'identity/setPrimaryIdentity/rejected', meta: { traceId } }, { error: error.message });
      return rejectWithValue(error.message);
    }
  }
);

export const unlinkIdentity = createAsyncThunk(
  'identity/unlinkIdentity',
  async ({ identityId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'identity/unlinkIdentity/pending', meta: { traceId } }, { identityId });
      await identityApi.unlinkIdentity(identityId, { traceId });
      logAction({ type: 'identity/unlinkIdentity/fulfilled', meta: { traceId } });
      return { identityId };
    } catch (error) {
      logAction({ type: 'identity/unlinkIdentity/rejected', meta: { traceId } }, { error: error.message });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMetadata = createAsyncThunk(
  'identity/fetchMetadata',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();
    try {
      logAction({ type: 'identity/fetchMetadata/pending', meta: { traceId } });
      const data = await identityApi.getMetadata({ traceId });
      logAction({ type: 'identity/fetchMetadata/fulfilled', meta: { traceId } });
      return data;
    } catch (error) {
      logAction({ type: 'identity/fetchMetadata/rejected', meta: { traceId } }, { error: error.message });
      return rejectWithValue(error.message);
    }
  }
);

export const setActiveMetadata = createAsyncThunk(
  'identity/setActiveMetadata',
  async ({ fieldName, historyId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'identity/setActiveMetadata/pending', meta: { traceId } }, { fieldName, historyId });
      await identityApi.setActiveMetadata(fieldName, historyId, { traceId });
      logAction({ type: 'identity/setActiveMetadata/fulfilled', meta: { traceId } });
      return { fieldName, historyId };
    } catch (error) {
      logAction({ type: 'identity/setActiveMetadata/rejected', meta: { traceId } }, { error: error.message });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMergeRequests = createAsyncThunk(
  'identity/fetchMergeRequests',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();
    try {
      logAction({ type: 'identity/fetchMergeRequests/pending', meta: { traceId } });
      const data = await identityApi.listMergeRequests({ traceId });
      logAction({ type: 'identity/fetchMergeRequests/fulfilled', meta: { traceId } });
      return data;
    } catch (error) {
      logAction({ type: 'identity/fetchMergeRequests/rejected', meta: { traceId } }, { error: error.message });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMergeRequest = createAsyncThunk(
  'identity/fetchMergeRequest',
  async ({ mergeId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'identity/fetchMergeRequest/pending', meta: { traceId } }, { mergeId });
      const data = await identityApi.getMergeRequest(mergeId, { traceId });
      logAction({ type: 'identity/fetchMergeRequest/fulfilled', meta: { traceId } });
      return data;
    } catch (error) {
      logAction({ type: 'identity/fetchMergeRequest/rejected', meta: { traceId } }, { error: error.message });
      return rejectWithValue(error.message);
    }
  }
);

export const approveMerge = createAsyncThunk(
  'identity/approveMerge',
  async ({ mergeId, targetUserId, fieldChoices, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'identity/approveMerge/pending', meta: { traceId } }, { mergeId, targetUserId });
      const data = await identityApi.approveMerge(mergeId, targetUserId, fieldChoices, { traceId });
      logAction({ type: 'identity/approveMerge/fulfilled', meta: { traceId } });
      return data;
    } catch (error) {
      logAction({ type: 'identity/approveMerge/rejected', meta: { traceId } }, { error: error.message });
      return rejectWithValue(error.message);
    }
  }
);

export const rejectMerge = createAsyncThunk(
  'identity/rejectMerge',
  async ({ mergeId, reason, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'identity/rejectMerge/pending', meta: { traceId } }, { mergeId });
      await identityApi.rejectMerge(mergeId, reason, { traceId });
      logAction({ type: 'identity/rejectMerge/fulfilled', meta: { traceId } });
      return { mergeId };
    } catch (error) {
      logAction({ type: 'identity/rejectMerge/rejected', meta: { traceId } }, { error: error.message });
      return rejectWithValue(error.message);
    }
  }
);

// ---- Slice ----

const initialState = {
  // Identities
  identities: [],
  identitiesLoading: false,
  identitiesError: null,

  // Metadata
  metadata: [],
  metadataLoading: false,
  metadataError: null,

  // Merge requests
  mergeRequests: [],
  mergeRequestsLoading: false,
  mergeRequestsError: null,

  // Current merge (for wizard)
  currentMerge: null,
  currentMergeLoading: false,
  currentMergeError: null,

  // Linking state
  linkingProvider: null,
  linkingUrl: null,
  linkingState: null,

  // UI state
  showLinkingModal: false,
  showMergeWizard: false,
};

const identitySlice = createSlice({
  name: 'identity',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.identitiesError = null;
      state.metadataError = null;
      state.mergeRequestsError = null;
      state.currentMergeError = null;
    },
    openLinkingModal: (state) => {
      state.showLinkingModal = true;
    },
    closeLinkingModal: (state) => {
      state.showLinkingModal = false;
      state.linkingProvider = null;
      state.linkingUrl = null;
      state.linkingState = null;
    },
    openMergeWizard: (state, action) => {
      state.showMergeWizard = true;
      state.currentMerge = action.payload;
    },
    closeMergeWizard: (state) => {
      state.showMergeWizard = false;
      state.currentMerge = null;
    },
    setLinkingProvider: (state, action) => {
      state.linkingProvider = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchIdentities
      .addCase(fetchIdentities.pending, (state) => {
        state.identitiesLoading = true;
        state.identitiesError = null;
      })
      .addCase(fetchIdentities.fulfilled, (state, action) => {
        state.identitiesLoading = false;
        state.identities = action.payload.identities || [];
      })
      .addCase(fetchIdentities.rejected, (state, action) => {
        state.identitiesLoading = false;
        state.identitiesError = action.payload;
      })

      // initiateLinking
      .addCase(initiateLinking.pending, (state) => {
        state.identitiesLoading = true;
      })
      .addCase(initiateLinking.fulfilled, (state, action) => {
        state.identitiesLoading = false;
        state.linkingUrl = action.payload.auth_url;
        state.linkingState = action.payload.state;
      })
      .addCase(initiateLinking.rejected, (state, action) => {
        state.identitiesLoading = false;
        state.identitiesError = action.payload;
      })

      // setPrimaryIdentity
      .addCase(setPrimaryIdentity.pending, (state) => {
        state.identitiesLoading = true;
      })
      .addCase(setPrimaryIdentity.fulfilled, (state, action) => {
        state.identitiesLoading = false;
        const updatedIdentity = action.payload.identity;
        state.identities = state.identities.map(id => ({
          ...id,
          is_primary: id.identity_id === updatedIdentity.identity_id,
        }));
      })
      .addCase(setPrimaryIdentity.rejected, (state, action) => {
        state.identitiesLoading = false;
        state.identitiesError = action.payload;
      })

      // unlinkIdentity
      .addCase(unlinkIdentity.pending, (state) => {
        state.identitiesLoading = true;
      })
      .addCase(unlinkIdentity.fulfilled, (state, action) => {
        state.identitiesLoading = false;
        state.identities = state.identities.filter(
          id => id.identity_id !== action.payload.identityId
        );
      })
      .addCase(unlinkIdentity.rejected, (state, action) => {
        state.identitiesLoading = false;
        state.identitiesError = action.payload;
      })

      // fetchMetadata
      .addCase(fetchMetadata.pending, (state) => {
        state.metadataLoading = true;
        state.metadataError = null;
      })
      .addCase(fetchMetadata.fulfilled, (state, action) => {
        state.metadataLoading = false;
        state.metadata = action.payload.fields || [];
      })
      .addCase(fetchMetadata.rejected, (state, action) => {
        state.metadataLoading = false;
        state.metadataError = action.payload;
      })

      // setActiveMetadata
      .addCase(setActiveMetadata.pending, (state) => {
        state.metadataLoading = true;
      })
      .addCase(setActiveMetadata.fulfilled, (state, action) => {
        state.metadataLoading = false;
        const { fieldName, historyId } = action.payload;
        state.metadata = state.metadata.map(field => {
          if (field.field_name === fieldName) {
            return {
              ...field,
              values: field.values.map(v => ({
                ...v,
                is_active: v.history_id === historyId,
              })),
            };
          }
          return field;
        });
      })
      .addCase(setActiveMetadata.rejected, (state, action) => {
        state.metadataLoading = false;
        state.metadataError = action.payload;
      })

      // fetchMergeRequests
      .addCase(fetchMergeRequests.pending, (state) => {
        state.mergeRequestsLoading = true;
        state.mergeRequestsError = null;
      })
      .addCase(fetchMergeRequests.fulfilled, (state, action) => {
        state.mergeRequestsLoading = false;
        state.mergeRequests = action.payload.merge_requests || [];
      })
      .addCase(fetchMergeRequests.rejected, (state, action) => {
        state.mergeRequestsLoading = false;
        state.mergeRequestsError = action.payload;
      })

      // fetchMergeRequest
      .addCase(fetchMergeRequest.pending, (state) => {
        state.currentMergeLoading = true;
        state.currentMergeError = null;
      })
      .addCase(fetchMergeRequest.fulfilled, (state, action) => {
        state.currentMergeLoading = false;
        state.currentMerge = action.payload;
      })
      .addCase(fetchMergeRequest.rejected, (state, action) => {
        state.currentMergeLoading = false;
        state.currentMergeError = action.payload;
      })

      // approveMerge
      .addCase(approveMerge.pending, (state) => {
        state.currentMergeLoading = true;
      })
      .addCase(approveMerge.fulfilled, (state, action) => {
        state.currentMergeLoading = false;
        state.showMergeWizard = false;
        state.currentMerge = null;
        // Remove from list
        state.mergeRequests = state.mergeRequests.filter(
          m => m.merge_id !== action.payload.merge_id
        );
      })
      .addCase(approveMerge.rejected, (state, action) => {
        state.currentMergeLoading = false;
        state.currentMergeError = action.payload;
      })

      // rejectMerge
      .addCase(rejectMerge.pending, (state) => {
        state.currentMergeLoading = true;
      })
      .addCase(rejectMerge.fulfilled, (state, action) => {
        state.currentMergeLoading = false;
        state.showMergeWizard = false;
        state.currentMerge = null;
        // Remove from list
        state.mergeRequests = state.mergeRequests.filter(
          m => m.merge_id !== action.payload.mergeId
        );
      })
      .addCase(rejectMerge.rejected, (state, action) => {
        state.currentMergeLoading = false;
        state.currentMergeError = action.payload;
      });
  },
});

export const {
  clearErrors,
  openLinkingModal,
  closeLinkingModal,
  openMergeWizard,
  closeMergeWizard,
  setLinkingProvider,
} = identitySlice.actions;

export default identitySlice.reducer;
